import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "../common/DatePicker";
import Button from "../common/Button";
import { formatDate, toRegionalDateFromISO, toISODateFromRegional, todayIso } from "../../utils/date";
import { localizeBatchName } from '../../utils/displayNames';

export const STUDENT_ARCHIVE_MODES = {
  END_ENROLLMENTS_ONLY: "END_ENROLLMENTS_ONLY",
  END_ENROLLMENTS_AND_ARCHIVE: "END_ENROLLMENTS_AND_ARCHIVE",
  ARCHIVE_ACCOUNT_ONLY: "ARCHIVE_ACCOUNT_ONLY",
};

export default function ArchiveStudentModal({
  open,
  userId,
  studentName,
  archiveCheck,
  loadingCheck = false,
  submitting = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_AND_ARCHIVE);
  const [endDateUI, setEndDateUI] = useState("");
  const [confirmAdvanced, setConfirmAdvanced] = useState(false);

  const enrollments = archiveCheck?.activeEnrollments ?? [];
  const hasActiveEnrollments = archiveCheck?.hasActiveEnrollments ?? enrollments.length > 0;

  useEffect(() => {
    if (!open) return;
    setMode(STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_AND_ARCHIVE);
    setConfirmAdvanced(false);
    const list = archiveCheck?.activeEnrollments ?? [];
    const suggested =
      list.find((e) => e.endEnrollmentDate)?.endEnrollmentDate
      || todayIso();
    setEndDateUI(toRegionalDateFromISO(suggested));
  }, [open, archiveCheck]);

  if (!open) return null;

  const endsEnrollments =
    mode === STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_ONLY
    || mode === STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_AND_ARCHIVE;

  const handleConfirm = () => {
    const endEnrollmentDate = endsEnrollments ? toISODateFromRegional(endDateUI) : null;
    if (endsEnrollments && !endEnrollmentDate) return;
    onConfirm?.({
      mode,
      endEnrollmentDate,
      confirmArchiveWithActiveEnrollments:
        mode === STUDENT_ARCHIVE_MODES.ARCHIVE_ACCOUNT_ONLY ? confirmAdvanced : undefined,
    });
  };

  const confirmDisabled =
    submitting
    || loadingCheck
    || (endsEnrollments && !endDateUI)
    || (mode === STUDENT_ARCHIVE_MODES.ARCHIVE_ACCOUNT_ONLY && hasActiveEnrollments && !confirmAdvanced);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {hasActiveEnrollments ? t('archiveStudent.titleWithEnrollments') : t('archiveStudent.titleSimple')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {hasActiveEnrollments ? (
            <>
              {t('archiveStudent.hasEnrollments', { name: studentName || t('archiveStudent.thisStudent'), count: enrollments.length })}
              {' '}{t('archiveStudent.chooseProceed')}
            </>
          ) : (
            <>{t('archiveStudent.archiveSimple', { name: studentName || t('archiveStudent.thisStudentLower') })}</>
          )}
        </p>

        {loadingCheck && (
          <p className="text-sm text-gray-500 mb-4">{t('archiveStudent.loadingEnrollments')}</p>
        )}

        {hasActiveEnrollments && !loadingCheck && (
          <>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-4 text-sm">
              <p className="font-medium text-gray-800 mb-2">{t('archiveStudent.activeEnrollments')}</p>
              <ul className="space-y-2">
                {enrollments.map((e) => (
                  <li key={e.enrollmentId} className="text-gray-700">
                    <span className="font-medium">{localizeBatchName(e.batchName, t)}</span>
                    <span className="text-gray-500"> · {e.levelName}</span>
                    {e.enrolledDate && (
                      <span className="block text-xs text-gray-500">
                        {formatDate(e.enrolledDate)}
                        {e.endEnrollmentDate ? ` → ${formatDate(e.endEnrollmentDate)}` : ` → ${t('archiveStudent.ongoing')}`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <fieldset className="space-y-3 mb-4">
              <legend className="text-sm font-medium text-gray-800 mb-2">{t('archiveStudent.whatHappens')}</legend>

              <label className="flex gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="archiveMode"
                  className="mt-1"
                  checked={mode === STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_ONLY}
                  onChange={() => setMode(STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_ONLY)}
                />
                <span>
                  <span className="block font-medium text-gray-900">{t('archiveStudent.endEnrollmentsOnly')}</span>
                  <span className="block text-sm text-gray-600">{t('archiveStudent.endEnrollmentsOnlyDesc')}</span>
                </span>
              </label>

              <label className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3 cursor-pointer">
                <input
                  type="radio"
                  name="archiveMode"
                  className="mt-1"
                  checked={mode === STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_AND_ARCHIVE}
                  onChange={() => setMode(STUDENT_ARCHIVE_MODES.END_ENROLLMENTS_AND_ARCHIVE)}
                />
                <span>
                  <span className="block font-medium text-gray-900">
                    {t('archiveStudent.endAndArchive')}
                    <span className="ml-1 text-xs font-normal text-blue-700">{t('archiveStudent.recommended')}</span>
                  </span>
                  <span className="block text-sm text-gray-600">{t('archiveStudent.endAndArchiveDesc')}</span>
                </span>
              </label>

              <label className="flex gap-3 rounded-lg border border-amber-200 p-3 cursor-pointer hover:bg-amber-50/30">
                <input
                  type="radio"
                  name="archiveMode"
                  className="mt-1"
                  checked={mode === STUDENT_ARCHIVE_MODES.ARCHIVE_ACCOUNT_ONLY}
                  onChange={() => setMode(STUDENT_ARCHIVE_MODES.ARCHIVE_ACCOUNT_ONLY)}
                />
                <span>
                  <span className="block font-medium text-gray-900">{t('archiveStudent.archiveOnly')}</span>
                  <span className="block text-sm text-gray-600">{t('archiveStudent.archiveOnlyDesc')}</span>
                </span>
              </label>
            </fieldset>

            {endsEnrollments && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('archiveStudent.endDateLabel')}
                </label>
                <DatePicker value={endDateUI} onChange={setEndDateUI} className="w-full" />
              </div>
            )}

            {mode === STUDENT_ARCHIVE_MODES.ARCHIVE_ACCOUNT_ONLY && (
              <label className="flex gap-2 items-start rounded-lg border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-950">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmAdvanced}
                  onChange={(e) => setConfirmAdvanced(e.target.checked)}
                />
                <span>
                  {t('archiveStudent.archiveOnlyConfirm')}
                </span>
              </label>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={confirmDisabled}>
            {submitting ? t('archiveStudent.processing') : hasActiveEnrollments ? t('archiveStudent.continue') : t('archiveStudent.archive')}
          </Button>
        </div>
      </div>
    </div>
  );
}
